import { isProviderId, providerDisplayName } from "./providers";

// Tags are the categories agents give loops. LoopGroup never infers them; it only makes them usable.

/** Tags that describe state or mechanics rather than a category. They stay on the loop but out of facets. */
export const STATE_TAGS = new Set([
  "active", "paused", "disabled", "enabled", "archived", "draft", "unknown",
  "automation", "workflow", "cron", "monitor", "routine", "task", "job", "script", "manual", "scheduled",
  "codex", "claude", "grok", "muse", "dots", "openai", "anthropic", "xai", "meta", "meta-ai", "cursor", "grok-bot", "openai-dots", "codex-desktop",
  "runtime-unverified", "unverified", "verified", "confirmed", "inferred", "read-only", "review-only", "configuration", "catalog",
]);

export function normalizeTag(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9._&+/-]/g, "").slice(0, 40);
}

export function isCategoryTag(tag: string): boolean {
  const normalized = normalizeTag(tag);
  return normalized.length > 0 && !STATE_TAGS.has(normalized);
}

export interface TagFacet {
  tag: string;
  count: number;
}

/** Category tags across a set of loops with their counts, most used first, then alphabetical. */
export function tagFacets(loops: Array<{ tags: string[] }>, limit = 24): TagFacet[] {
  const counts = new Map<string, number>();
  for (const loop of loops) {
    const seen = new Set<string>();
    for (const raw of loop.tags) {
      const tag = normalizeTag(raw);
      if (!isCategoryTag(tag) || seen.has(tag)) continue;
      seen.add(tag);
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
    .slice(0, limit);
}

/** The attribute a sharing rule or a bulk filter matches on. */
export type LoopAttribute = "tag" | "source" | "kind" | "cadence";

export interface AttributeLoop {
  tags: string[];
  sourceId: string | null;
  kind: string | null;
  cadence: string;
}

/** Whether a loop matches an attribute value; tags compare normalized, the rest compare case-insensitively. */
export function matchesAttribute(loop: AttributeLoop, attribute: LoopAttribute, value: string): boolean {
  const wanted = value.trim().toLowerCase();
  if (!wanted) return false;
  if (attribute === "tag") return loop.tags.some((tag) => normalizeTag(tag) === normalizeTag(value));
  if (attribute === "source") return (loop.sourceId ?? "").toLowerCase() === wanted;
  if (attribute === "kind") return (loop.kind ?? "").trim().toLowerCase() === wanted;
  return loop.cadence.trim().toLowerCase() === wanted;
}

export function describeRule(attribute: LoopAttribute, value: string, sourceName?: string | null): string {
  if (attribute === "tag") return `every loop tagged ${value}`;
  if (attribute === "source") return `every loop from ${sourceName ?? "this source"}`;
  if (attribute === "kind") return `every ${value} loop`;
  return `every loop that runs ${value}`;
}

function agentName(provider: string): string {
  return isProviderId(provider) && provider !== "other" ? providerDisplayName(provider) : "your agent";
}

/**
 * The prompt a person pastes into their agent to categorize the catalog. The agent reads the loops it
 * already knows, proposes a small vocabulary, waits for approval, then applies tags with tag_loops.
 */
export function buildCategorizePrompt(provider: string): string {
  const name = agentName(provider);
  return `Help me categorize my LoopGroup catalog so I can share loops in groups instead of one at a time. Use the existing LoopGroup connection in this environment (call get_connection_status first and stop if it is not claimed).

DO NOT CHANGE ANYTHING YET
1. Call list_loops and read every loop's name, description, source, kind, cadence, and current tags.
2. Propose a small vocabulary of category tags: the project, team, client, or domain each loop serves. Aim for five to twelve tags across the whole catalog. Use short lowercase slugs such as "peptide-app", "finance", "seo". Do not propose state words ("active", "paused"), agent names ("codex"), or kinds ("automation"); LoopGroup keeps those separately.
3. Show me the proposed tags with the loops under each, and which existing tags you would keep, rename, or drop.

Wait for my approval. Then apply exactly what I approved with tag_loops (add and remove tags per loop) in batches, and finish with a count of loops changed and any loop you could not place. Never edit the loops themselves, their schedules, or anything in ${name} outside LoopGroup.`;
}
